import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search } from 'lucide-react';

// Lista de paises con bandera (emoji), nombre y codigo telefonico.
// Ordenada: Mexico primero, luego LATAM, luego resto del mundo.
export const COUNTRIES = [
    { code: 'MX', name: 'Mexico', dial: '52', flag: '🇲🇽' },
    { code: 'US', name: 'Estados Unidos', dial: '1', flag: '🇺🇸' },
    { code: 'CA', name: 'Canada', dial: '1', flag: '🇨🇦' },
    { code: 'GT', name: 'Guatemala', dial: '502', flag: '🇬🇹' },
    { code: 'HN', name: 'Honduras', dial: '504', flag: '🇭🇳' },
    { code: 'SV', name: 'El Salvador', dial: '503', flag: '🇸🇻' },
    { code: 'NI', name: 'Nicaragua', dial: '505', flag: '🇳🇮' },
    { code: 'CR', name: 'Costa Rica', dial: '506', flag: '🇨🇷' },
    { code: 'PA', name: 'Panama', dial: '507', flag: '🇵🇦' },
    { code: 'BZ', name: 'Belice', dial: '501', flag: '🇧🇿' },
    { code: 'CU', name: 'Cuba', dial: '53', flag: '🇨🇺' },
    { code: 'DO', name: 'Republica Dominicana', dial: '1', flag: '🇩🇴' },
    { code: 'PR', name: 'Puerto Rico', dial: '1', flag: '🇵🇷' },
    { code: 'HT', name: 'Haiti', dial: '509', flag: '🇭🇹' },
    { code: 'JM', name: 'Jamaica', dial: '1', flag: '🇯🇲' },
    { code: 'CO', name: 'Colombia', dial: '57', flag: '🇨🇴' },
    { code: 'VE', name: 'Venezuela', dial: '58', flag: '🇻🇪' },
    { code: 'EC', name: 'Ecuador', dial: '593', flag: '🇪🇨' },
    { code: 'PE', name: 'Peru', dial: '51', flag: '🇵🇪' },
    { code: 'BO', name: 'Bolivia', dial: '591', flag: '🇧🇴' },
    { code: 'CL', name: 'Chile', dial: '56', flag: '🇨🇱' },
    { code: 'AR', name: 'Argentina', dial: '54', flag: '🇦🇷' },
    { code: 'UY', name: 'Uruguay', dial: '598', flag: '🇺🇾' },
    { code: 'PY', name: 'Paraguay', dial: '595', flag: '🇵🇾' },
    { code: 'BR', name: 'Brasil', dial: '55', flag: '🇧🇷' },
    { code: 'ES', name: 'Espana', dial: '34', flag: '🇪🇸' },
    { code: 'PT', name: 'Portugal', dial: '351', flag: '🇵🇹' },
    { code: 'FR', name: 'Francia', dial: '33', flag: '🇫🇷' },
    { code: 'IT', name: 'Italia', dial: '39', flag: '🇮🇹' },
    { code: 'DE', name: 'Alemania', dial: '49', flag: '🇩🇪' },
    { code: 'GB', name: 'Reino Unido', dial: '44', flag: '🇬🇧' },
];

export const DEFAULT_COUNTRY = COUNTRIES[0]; // Mexico

// Parsea un numero completo (ej. "525512345678") y separa lada + numero local
export const parsePhone = (full) => {
    const digits = (full || '').replace(/\D/g, '');
    if (!digits) return { country: DEFAULT_COUNTRY, local: '' };
    // Probar coincidencia con ladas mas largas primero
    const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
    for (const c of sorted) {
        if (digits.startsWith(c.dial)) {
            return { country: c, local: digits.slice(c.dial.length) };
        }
    }
    // Sin lada detectada: asumir Mexico
    return { country: DEFAULT_COUNTRY, local: digits };
};

// Construye el numero completo (lada + numero) sin signos
export const buildPhone = (country, local) => {
    const dial = (country?.dial || DEFAULT_COUNTRY.dial);
    const num = (local || '').replace(/\D/g, '');
    return num ? `${dial}${num}` : '';
};

/**
 * PhoneInput: input de telefono con selector de pais (lada).
 *
 * Props:
 *   - value: numero completo en formato "52551234..." (sin +)
 *   - onChange: (newFullNumber) => void
 *   - placeholder: placeholder del numero local
 *   - className: clases extra para el wrapper
 *   - inputClassName: clases extra para el input
 *   - testId: data-testid base
 */
export const PhoneInput = ({ value, onChange, placeholder = 'Numero', className = '', inputClassName = '', testId = 'phone' }) => {
    const parsed = parsePhone(value);
    const [country, setCountry] = useState(parsed.country);
    const [local, setLocal] = useState(parsed.local);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef(null);

    // Mantener sincronizado si value externo cambia
    useEffect(() => {
        const p = parsePhone(value);
        // Solo actualizar si el numero local realmente cambia (evita loops)
        if (p.country.code !== country.code || p.local !== local) {
            setCountry(p.country);
            setLocal(p.local);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const fire = (c, l) => {
        onChange?.(buildPhone(c, l));
    };

    const onCountryChange = (c) => {
        setCountry(c);
        setOpen(false);
        setSearch('');
        fire(c, local);
    };

    const onLocalChange = (e) => {
        const v = e.target.value.replace(/\D/g, '');
        setLocal(v);
        fire(country, v);
    };

    const filtered = COUNTRIES.filter(c => {
        if (!search) return true;
        const q = search.toLowerCase();
        return c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.toLowerCase().includes(q);
    });

    return (
        <div ref={containerRef} className={`relative flex gap-2 ${className}`}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-sm text-zinc-100 transition-colors shrink-0"
                data-testid={`${testId}-country-trigger`}
                title={country.name}
            >
                <span className="text-base leading-none">{country.flag}</span>
                <span className="font-mono text-xs">+{country.dial}</span>
                <ChevronDown className="w-3 h-3 text-zinc-400" />
            </button>
            <input
                type="tel"
                inputMode="numeric"
                value={local}
                onChange={onLocalChange}
                placeholder={placeholder}
                className={`flex-1 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-amber-500 ${inputClassName}`}
                data-testid={`${testId}-input`}
            />

            {open && (
                <div className="absolute top-full left-0 mt-1 w-72 max-w-[90vw] bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl z-50 max-h-80 overflow-hidden flex flex-col" data-testid={`${testId}-country-list`}>
                    <div className="p-2 border-b border-zinc-700 relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                        <input
                            type="text"
                            placeholder="Buscar pais..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-8 pr-2 py-1.5 rounded bg-zinc-800 border border-zinc-700 text-xs text-zinc-100 placeholder-zinc-500 focus:outline-none"
                            autoFocus
                        />
                    </div>
                    <div className="overflow-y-auto flex-1">
                        {filtered.length === 0 ? (
                            <div className="px-3 py-4 text-xs text-zinc-500 text-center">Sin resultados</div>
                        ) : filtered.map(c => (
                            <button
                                key={c.code}
                                type="button"
                                onClick={() => onCountryChange(c)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-800 transition-colors"
                                data-testid={`${testId}-country-${c.code}`}
                            >
                                <span className="text-lg leading-none">{c.flag}</span>
                                <span className="flex-1 text-zinc-100 truncate">{c.name}</span>
                                <span className="text-xs text-zinc-500 font-mono">+{c.dial}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PhoneInput;
